/**
 * StartSequenceModal Component
 * Modal for manually enrolling ONE contact into an existing ACTIVE message sequence, from the contact
 * record (post-support follow-ups). Enrollment runs through the role-gated `enroll-contact-in-sequence`
 * edge function (the authoritative enroll RPC is service_role-only). This does NOT change the sequence's
 * own criteria — it adds only this one contact.
 *
 * Reports honest outcomes: enrolled, or the specific guard message (already enrolled, sequence not
 * active, re-enrollment not allowed, no starting step, missing date field) — never a false success.
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Modal from "../common/Modal";
import { useToast } from "../ui/Toast";
import {
  getActiveSequences,
  enrollContactInSequence,
} from "../../services/sequences.service";
import type { Contact } from "../../types/contact.types";

interface StartSequenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact;
}

const StartSequenceModal: React.FC<StartSequenceModalProps> = ({
  isOpen,
  onClose,
  contact,
}) => {
  const { showToast } = useToast();
  const [selectedId, setSelectedId] = useState<string>("");
  const [isEnrolling, setIsEnrolling] = useState(false);

  const {
    data: sequences,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["active-sequences"],
    queryFn: getActiveSequences,
    enabled: isOpen,
  });

  const handleClose = () => {
    setSelectedId("");
    onClose();
  };

  const handleEnroll = async () => {
    if (!selectedId) {
      showToast("error", "Please choose a sequence to start.");
      return;
    }
    setIsEnrolling(true);
    try {
      const outcome = await enrollContactInSequence(contact.id, selectedId);
      if (outcome.status === "enrolled") {
        showToast(
          "success",
          outcome.message || "Contact enrolled in the sequence.",
        );
        setSelectedId("");
        onClose();
      } else {
        // Guard refusal or failure — show the specific, actionable message. Not a false success.
        showToast("error", outcome.message || "Could not start the sequence.");
      }
    } finally {
      setIsEnrolling(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Start a Sequence"
      size="lg"
    >
      <div className="space-y-4">
        {/* Contact info */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-sm text-gray-500">Enroll:</div>
          <div className="font-medium text-gray-900">
            {contact.first_name} {contact.last_name}
          </div>
          <div className="text-sm text-gray-600">
            {contact.email || "No email address"}
          </div>
        </div>

        {/* Sequence picker */}
        <div>
          <label
            htmlFor="start-sequence-select"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Active sequence
          </label>
          {isLoading ? (
            <div className="text-sm text-gray-500">Loading sequences…</div>
          ) : error ? (
            <div className="text-sm text-red-600">
              Could not load sequences. Please close and try again.
            </div>
          ) : !sequences || sequences.length === 0 ? (
            <div className="text-sm text-gray-500">
              There are no active sequences to start. Activate a sequence first.
            </div>
          ) : (
            <select
              id="start-sequence-select"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a sequence…</option>
              {sequences.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Info notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-blue-600 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm text-blue-800">
              This adds only this contact to the selected sequence — it does not
              change who else the sequence enrolls. If the contact is already
              enrolled, we'll tell you and nothing is duplicated.
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleEnroll}
            disabled={!selectedId || isEnrolling}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isEnrolling ? "Starting…" : "Start Sequence"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default StartSequenceModal;
